import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import {
  CURRENCY_SYMBOLS,
  REGION_CURRENCY,
  PricingRegion,
  SubscriptionPlan,
} from '../types/subscription';
import { isEarlyBirdActive } from '../config/earlyBirdEvent';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5555';
const PROMO_DISCOUNT = 0.40;

const PLAN_SECTIONS = [
  { id: 'pronunciation', label: 'Pronunciation', icon: '🎤', color: 'blue' },
  { id: 'ielts', label: 'IELTS', icon: '📚', color: 'emerald' },
  { id: 'interview', label: 'Interview', icon: '💼', color: 'purple' },
] as const;

export function usePricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [region, setRegion] = useState<PricingRegion>('ca');
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null);
  const [activeSection, setActiveSection] = useState<string>('pronunciation');

  const { currentUser } = useAuth();
  const { plan: currentPlan, createStripeCheckout, createStripePortal } = useSubscription();
  const navigate = useNavigate();

  // Currently only supporting CAD pricing
  useEffect(() => {
    setRegion('ca');
  }, []);

  // Auto-apply promo discount when Early Bird event is active
  const earlyBirdActive = isEarlyBirdActive();
  useEffect(() => {
    if (earlyBirdActive && !promoApplied) {
      setPromoCode('EARLYBIRD');
      setPromoApplied(true);
    }
  }, [earlyBirdActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = PLAN_SECTIONS.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(PLAN_SECTIONS[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  }, []);

  const currency = REGION_CURRENCY[region];
  const currencySymbol = CURRENCY_SYMBOLS[currency];

  const getPrice = useCallback((basePrice: number): number => {
    if (basePrice === 0) return 0;
    if (promoApplied) return basePrice;
    return basePrice / (1 - PROMO_DISCOUNT);
  }, [promoApplied]);

  const handleApplyPromo = useCallback(async () => {
    setPromoError('');
    setPromoValidating(true);

    try {
      const response = await fetch(`${API_BASE}/api/subscriptions/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promoCode: promoCode.trim(),
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
        }),
      });

      const data = await response.json();

      if (data.valid) {
        setPromoApplied(true);
        setPromoError('');
      } else {
        setPromoError(data.message || 'Invalid promo code');
        setPromoApplied(false);
      }
    } catch (error) {
      console.error('Failed to validate promo code:', error);
      setPromoError('Failed to validate promo code');
      setPromoApplied(false);
    } finally {
      setPromoValidating(false);
    }
  }, [promoCode, currentUser]);

  const handleRemovePromo = useCallback(() => {
    setPromoCode('');
    setPromoApplied(false);
    setPromoError('');
  }, []);

  const proceedToCheckout = useCallback(async (planId: SubscriptionPlan) => {
    setIsLoading(planId);
    try {
      const successUrl = `${window.location.origin}/payment/success`;
      const cancelUrl = `${window.location.origin}/pricing`;
      const checkoutUrl = await createStripeCheckout(planId, billingCycle, successUrl, cancelUrl);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(null);
    }
  }, [billingCycle, createStripeCheckout]);

  const handlePromoApplied = useCallback((code: string) => {
    setPromoCode(code);
    setPromoApplied(true);
    setPromoError('');
    if (pendingPlan) {
      proceedToCheckout(pendingPlan);
      setPendingPlan(null);
    }
  }, [pendingPlan, proceedToCheckout]);

  const handleSubscribe = useCallback(async (planId: SubscriptionPlan) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (planId === 'free') {
      navigate('/app');
      return;
    }

    // Skip promo modal when Early Bird is active (discount already applied)
    if (!promoApplied && !earlyBirdActive) {
      setPendingPlan(planId);
      setShowPromoModal(true);
      return;
    }

    proceedToCheckout(planId);
  }, [currentUser, promoApplied, earlyBirdActive, proceedToCheckout, navigate]);

  const handleManageSubscription = useCallback(async () => {
    setIsLoading('manage');
    try {
      const returnUrl = `${window.location.origin}/pricing`;
      const portalUrl = await createStripePortal(returnUrl);
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setIsLoading(null);
    }
  }, [createStripePortal]);

  const getButtonText = useCallback((planId: SubscriptionPlan) => {
    if (currentUser && currentPlan === planId) {
      return 'Current Plan';
    }
    if (planId === 'free') {
      return 'Get Started Free';
    }
    return 'Subscribe Now';
  }, [currentUser, currentPlan]);

  const isCurrentPlan = useCallback((planId: SubscriptionPlan) => {
    return currentUser && currentPlan === planId;
  }, [currentUser, currentPlan]);

  const openPromoModal = useCallback(() => {
    setShowPromoModal(true);
  }, []);

  const closePromoModal = useCallback(() => {
    setShowPromoModal(false);
    setPendingPlan(null);
  }, []);

  return {
    billingCycle,
    setBillingCycle,
    region,
    setRegion,
    isLoading,
    promoCode,
    setPromoCode,
    promoApplied,
    promoError,
    setPromoError,
    promoValidating,
    showPromoModal,
    activeSection,
    currentUser,
    currentPlan,
    currency,
    currencySymbol,
    planSections: PLAN_SECTIONS,
    earlyBirdActive,
    promoDiscount: PROMO_DISCOUNT,
    getPrice,
    handleApplyPromo,
    handleRemovePromo,
    handlePromoApplied,
    handleSubscribe,
    handleManageSubscription,
    getButtonText,
    isCurrentPlan,
    scrollToSection,
    openPromoModal,
    closePromoModal,
  };
}
